import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { getJobMembers, getMentionableUsers, setJobMembers } from "../../../lib/api";
import type { MemberView, MentionableUser } from "../types";
import form from "./NewJobForm.module.css";
import styles from "./MemberField.module.css";

/** How many picker rows show at once. */
const CANDIDATE_LIMIT = 8;

export interface MemberFieldProps {
  /** Persisted job id — the field only renders in edit mode. */
  jobId: string;
}

/**
 * Card members (job-collaboration epic): the people attached to a job for
 * attribution (DEC-JC-003). Server data, not draft fields — the list loads
 * from `GET /api/jobs/:id/members` on mount and every add/remove replaces the
 * whole list via `PUT` (the documents-section pattern), so changes persist
 * without touching the form's submit. Candidates come from the same per-job
 * mentionable pool the @-autocomplete uses (Active staff + the job's client
 * org's portal users; DEC-JC-004).
 */
export function MemberField({ jobId }: MemberFieldProps) {
  const [members, setMembers] = useState<MemberView[]>([]);
  const [candidates, setCandidates] = useState<MentionableUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const adderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getJobMembers(jobId), getMentionableUsers(jobId)])
      .then(([memberResult, candidateResult]) => {
        if (cancelled) return;
        setMembers(memberResult.members);
        setCandidates(candidateResult.users);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : "Could not load members");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  // Clicking anywhere outside the adder closes the picker; the input's blur
  // alone would close it before a row's click lands.
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: PointerEvent): void => {
      if (!adderRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  // Candidates not already on the card, filtered by the typed query.
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const memberIds = new Set(members.map((member) => member.userId));
    return candidates
      .filter(
        (candidate) =>
          !memberIds.has(candidate.id) &&
          (!needle ||
            candidate.displayName.toLowerCase().includes(needle) ||
            candidate.email.toLowerCase().includes(needle)),
      )
      .slice(0, CANDIDATE_LIMIT);
  }, [candidates, members, query]);

  /** Replaces the member list server-side; the response is the new truth. */
  const persist = async (ids: string[]): Promise<void> => {
    setSaving(true);
    setError("");
    try {
      const { members: next } = await setJobMembers(jobId, ids);
      setMembers(next);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not update members");
    } finally {
      setSaving(false);
    }
  };

  const addMember = (candidate: MentionableUser): void => {
    setQuery("");
    setOpen(false);
    void persist([...members.map((member) => member.userId), candidate.id]);
  };

  const removeMember = (userId: string): void => {
    void persist(members.filter((member) => member.userId !== userId).map((member) => member.userId));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (!open || matches.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      setHighlight((index) => (index + delta + matches.length) % matches.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      addMember(matches[Math.min(highlight, matches.length - 1)]);
      return;
    }
    if (event.key === "Escape") {
      // Only the picker closes; the job modal must stay open.
      event.stopPropagation();
      setOpen(false);
    }
  };

  const handleQueryChange = (value: string): void => {
    setQuery(value);
    setOpen(true);
    setHighlight(0);
  };

  return (
    <section className={form.section}>
      <h3 className={form.sectionTitle}>Members</h3>
      <p className={form.note}>People attached to this job for visibility; attribution only.</p>
      {error && <p className={styles.error}>{error}</p>}

      {members.length > 0 && (
        <ul className={styles.chips}>
          {members.map((member) => (
            <li key={member.userId} className={styles.chip}>
              <span className={styles.chipName} title={member.email}>
                {member.displayName || member.email}
              </span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => removeMember(member.userId)}
                disabled={saving}
                aria-label={`Remove ${member.displayName || member.email}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {loading && <p className={styles.empty}>Loading members…</p>}
      {!loading && members.length === 0 && <p className={styles.empty}>No members yet.</p>}

      <div className={styles.adder} ref={adderRef}>
        <input
          className={form.input}
          value={query}
          placeholder="Add a member — search name or email"
          aria-label="Add member"
          disabled={saving}
          onChange={(event) => handleQueryChange(event.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {open && matches.length > 0 && (
          <ul className={styles.list} role="listbox" aria-label="Member candidates">
            {matches.map((candidate, index) => (
              <li key={candidate.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === highlight}
                  className={index === highlight ? styles.rowActive : styles.row}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => addMember(candidate)}
                >
                  <span className={styles.rowName}>{candidate.displayName}</span>
                  <span className={styles.rowEmail}>
                    {candidate.kind === "portal" ? `${candidate.email} · portal` : candidate.email}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
